// Asks before anything crosses between the drive and Locked, naming what changes: going in, items
// are sealed and leave My Drive; coming out, they are unsealed and anyone with the session can open
// them. Folders go with everything inside them. Each secured file is re-encrypted for the side it
// goes to, which takes a moment after the move itself.
//
// The dialog only asks; the drive moves the items and says how that went.
import PropTypes from 'prop-types'

import Dialog from '@mui/material/Dialog'
import FolderIcon from '@mui/icons-material/FolderRounded'
import LockIcon from '@mui/icons-material/LockOutlined'
import UnlockIcon from '@mui/icons-material/LockOpenOutlined'

import {BaseButton} from '../shared/blocks/Buttons'
import {KIND_ICONS} from './DriveItems'
import {Text} from '../shared/blocks/Texts'

import {fileKind, formatBytes} from '../../lib/fileTools'

import './lockedDialog.scss'

const plural = count => `${count} item${count === 1 ? '' : 's'}`

const COPY = {
  'in': {
    Icon: LockIcon,
    title: 'Move to Locked',
    body: count => `${plural(count)} will be sealed and will leave My Drive. Folders move with everything inside them.`,
    confirm: 'Seal and move'
  },
  out: {
    Icon: UnlockIcon,
    title: 'Move out of Locked',
    body: count => `${plural(count)} will be unsealed and will appear in My Drive, where anyone with your session can open them.`,
    confirm: 'Move out'
  }
}

const LockedMoveDialog = ({direction, entries, onClose, onConfirm}) => {
  const {Icon, title, body, confirm} = COPY[direction]

  return (
    <Dialog open onClose={onClose} aria-labelledby='locked-move-title' slotProps={{paper: {className: 'locked-dialog'}}}>
      <div className='dialog-head'>
        <Icon />
        <h2 className='dialog-title' id='locked-move-title'>{title}</h2>
      </div>
      <Text className='dialog-body'>{body(entries.length)}</Text>

      <ul className='move-list' aria-label='Items'>
        {entries.map(({kind, id, name, filesize}) => {
          const Glyph = kind === 'folder' ? FolderIcon : KIND_ICONS[fileKind(name)]

          return (
            <li key={`${kind}:${id}`}>
              <Glyph fontSize='small' />
              <span>{name}</span>
              {typeof filesize === 'number' && <span className='move-list-size'>{formatBytes(filesize)}</span>}
            </li>
          )
        })}
      </ul>

      <div className='dialog-actions'>
        <BaseButton className='dialog-cancel' variant='text' onClick={onClose}>Cancel</BaseButton>
        <BaseButton className='dialog-confirm' color={direction === 'in' ? 'secondary' : 'primary'} onClick={onConfirm}>
          {confirm}
        </BaseButton>
      </div>
    </Dialog>
  )
}

LockedMoveDialog.propTypes = {
  // Into Locked, or out of it to the top of My Drive.
  direction: PropTypes.oneOf(['in', 'out']).isRequired,
  entries: PropTypes.arrayOf(PropTypes.shape({
    kind: PropTypes.oneOf(['folder', 'file']).isRequired,
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    filesize: PropTypes.number
  })).isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired
}

export default LockedMoveDialog
